import { useQuery } from "@tanstack/react-query";
import { AdminTopline, Panel } from "../../components/console";
import { api, authHeaders, describeError } from "../../lib/api";
import type { LoginAttempt } from "../../lib/api";
import { getCurrentRole } from "../../lib/auth";
import { ROLE_LABEL_LO } from "../../lib/roles";
import { num, shortId } from "../../lib/format";

const WINDOW_DAYS = 7;

const REASON_LABEL_LO: Record<string, string> = {
  bad_pin: "ລະຫັດ PIN ຜິດ",
  unknown_identifier: "ບໍ່ພົບບັນຊີ",
  locked: "ບັນຊີຖືກລັອກຢູ່",
};

async function fetchAttempts(): Promise<LoginAttempt[]> {
  const { data, error, response } = await api.GET("/v1/admin/system/login-attempts", {
    params: { query: { days: WINDOW_DAYS, limit: 200 } },
    headers: authHeaders(),
  });
  if (error || !data) throw new Error(describeError(error, response.status));
  return data.items;
}

function stamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** ผิดซ้ำถึงเท่านี้ใน WINDOW_DAYS = น่าสงสัยว่ามีคนสุ่มเดา PIN — ไฮไลต์ให้ผู้ดูแลเห็นก่อน */
const SUSPICIOUS_REPEATS = 5;

function countBy<T>(rows: T[], key: (row: T) => string): [string, number][] {
  const m = new Map<string, number>();
  for (const r of rows) m.set(key(r), (m.get(key(r)) ?? 0) + 1);
  return [...m.entries()].sort((a, b) => b[1] - a[1]);
}

function RankList({
  items,
  label,
  flagSuspicious = false,
}: {
  items: [string, number][];
  label: (key: string) => string;
  /** ระบายสีแดงให้แถวที่ผิดซ้ำถึงเกณฑ์ — ใช้กับรายบัญชีเท่านั้น ยอดรวมของสาเหตุไม่ใช่สัญญาณเตือน */
  flagSuspicious?: boolean;
}) {
  const max = items[0]?.[1] ?? 0;
  if (items.length === 0) return <p className="sec-rank__empty">ບໍ່ມີຂໍ້ມູນໃນໄລຍະນີ້</p>;
  return (
    <ol className="sec-rank">
      {items.map(([key, n]) => (
        <li key={key} className="sec-rank__row">
          <span className="sec-rank__label">{label(key)}</span>
          <span className="sec-rank__track" aria-hidden="true">
            <span
              className={flagSuspicious && n >= SUSPICIOUS_REPEATS ? "sec-rank__bar is-hot" : "sec-rank__bar"}
              style={{ width: `${String(max > 0 ? (n / max) * 100 : 0)}%` }}
            />
          </span>
          <span className="sec-rank__value num">{num(n)}</span>
        </li>
      ))}
    </ol>
  );
}

export function SystemSecurityPage() {
  const role = getCurrentRole();
  const attempts = useQuery({ queryKey: ["system", "login-attempts"], queryFn: fetchAttempts });

  const rows = attempts.data ?? [];
  // นับว่ารายการไหนซ้ำบ่อย — นี่คือสิ่งที่ผู้ดูแลต้องการเห็นจริง ๆ ไม่ใช่รายการดิบล้วน
  const byAccount = countBy(rows, (a) => a.identifier_hash);
  const repeats = new Map(byAccount);
  const byReason = countBy(rows, (a) => a.reason);
  const ipCount = new Set(rows.map((a) => a.ip).filter(Boolean)).size;
  const suspicious = byAccount.filter(([, n]) => n >= SUSPICIOUS_REPEATS).length;
  const locked = rows.filter((a) => a.reason === "locked").length;
  const dash = (v: number) => (attempts.isPending ? "—" : num(v));

  return (
    <div>
      <AdminTopline
        title="ເຂົ້າລະບົບລົ້ມເຫຼວ"
        subtitle={`ການເຂົ້າສູ່ລະບົບທີ່ລົ້ມເຫຼວ ${String(WINDOW_DAYS)} ວັນຫຼ້າສຸດ — ລະບົບລັອກບັນຊີອັດຕະໂນມັດເມື່ອໃສ່ PIN ຜິດຊ້ຳຫຼາຍຄັ້ງ`}
        roleLabel={role ? ROLE_LABEL_LO[role] : undefined}
      />

      {attempts.error ? (
        <div className="alert-error" style={{ marginBottom: "var(--space-4)" }}>
          ໂຫຼດຂໍ້ມູນບໍ່ສຳເລັດ: {attempts.error.message}
        </div>
      ) : null}

      <div className="kpi-grid">
        <article className="kpi">
          <div className="lbl">ຄັ້ງທີ່ລົ້ມເຫຼວທັງໝົດ</div>
          <div className="val num">{dash(rows.length)}</div>
          <div className="delta" style={{ color: "var(--ink-400)" }}>
            ພາຍໃນ {WINDOW_DAYS} ວັນ
          </div>
        </article>
        <article className="kpi">
          <div className="lbl">ບັນຊີທີ່ຖືກລອງ</div>
          <div className="val num">{dash(byAccount.length)}</div>
          <div className="delta" style={{ color: "var(--ink-400)" }}>
            ບັນຊີທີ່ແຕກຕ່າງກັນ
          </div>
        </article>
        <article className="kpi">
          <div className="lbl">ບັນຊີໜ້າສົງໄສ</div>
          <div className="val num" style={suspicious > 0 ? { color: "var(--error)" } : undefined}>
            {dash(suspicious)}
          </div>
          <div className="delta" style={{ color: "var(--ink-400)" }}>
            ຜິດຕັ້ງແຕ່ {SUSPICIOUS_REPEATS} ຄັ້ງຂຶ້ນໄປ
          </div>
        </article>
        <article className="kpi">
          <div className="lbl">ຖືກລັອກ / IP</div>
          <div className="val num">
            {dash(locked)} <span className="kpi__sep">/</span> {dash(ipCount)}
          </div>
          <div className="delta" style={{ color: "var(--ink-400)" }}>
            ຄັ້ງທີ່ຕິດລັອກ / IP ທີ່ແຕກຕ່າງ
          </div>
        </article>
      </div>

      <div className="grid2">
        <Panel title="ບັນຊີທີ່ຖືກລອງຫຼາຍທີ່ສຸດ" note="ສະແດງເປັນລະຫັດອ້າງອີງ — ລະບົບບໍ່ເກັບເບີໂທ/ອີເມວທີ່ໃຊ້ລອງ">
          <RankList items={byAccount.slice(0, 5)} label={(k) => shortId(k)} flagSuspicious />
        </Panel>
        <Panel title="ແຍກຕາມສາເຫດ">
          <RankList items={byReason} label={(k) => REASON_LABEL_LO[k] ?? k} />
        </Panel>
      </div>

      <Panel
        title="ລາຍການລົ້ມເຫຼວລ່າສຸດ"
        note="ລະບົບເກັບຂໍ້ມູນນີ້ໄວ້ 90 ວັນ ແລ້ວລຶບອັດຕະໂນມັດ"
      >
        {/* identifier เก็บเป็น hash — จอต้องการแค่ "รายการเดิมซ้ำกี่ครั้ง"
            ไม่ได้ต้องรู้ว่าเป็นเบอร์อะไร */}
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>ເວລາ</th>
                <th>ລະຫັດອ້າງອີງບັນຊີ</th>
                <th>ສາເຫດ</th>
                <th>IP</th>
                <th className="is-right">ຜິດໃນ {WINDOW_DAYS} ວັນ</th>
              </tr>
            </thead>
            <tbody>
              {attempts.isPending ? (
                <tr>
                  <td colSpan={5} className="data-table__empty">
                    ກຳລັງໂຫຼດ…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="data-table__empty">
                    ບໍ່ມີການເຂົ້າສູ່ລະບົບທີ່ລົ້ມເຫຼວໃນໄລຍະນີ້
                  </td>
                </tr>
              ) : (
                rows.map((a) => {
                  const n = repeats.get(a.identifier_hash) ?? 1;
                  return (
                    <tr key={a.id}>
                      <td className="num">{stamp(a.occurred_at)}</td>
                      <td className="num">{shortId(a.identifier_hash)}</td>
                      <td>
                        <span
                          className={
                            a.reason === "locked"
                              ? "status-badge status-badge--danger"
                              : "status-badge status-badge--pending"
                          }
                        >
                          {REASON_LABEL_LO[a.reason] ?? a.reason}
                        </span>
                      </td>
                      <td className="num">{a.ip || "—"}</td>
                      <td className="is-right">
                        {n >= SUSPICIOUS_REPEATS ? (
                          <span className="status-badge status-badge--danger sec-hot">
                            <span className="num">{num(n)}</span>
                            <span>ໜ້າສົງໄສ</span>
                          </span>
                        ) : (
                          <span className="num">{num(n)}</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
